import { TypeColumn } from "@inovua/reactdatagrid-community/types";
import { getColumnVisibilityFromStorage } from "./helpers";
import { DATA_GRID_COLUMN_VISIBILITY_STORAGE_KEY } from "utility/constants";

type CellSerializerContext = {
  getBrandNameById?: (id: number) => string;
  getProductTypeNameById?: (id: number) => string;
  getShippingLabelById?: (id: number) => string;
};

const formatDate = (value: string): string => {
  const formatted = new Date(value).toLocaleString("sv-SE");
  return formatted === "Invalid Date" ? "" : formatted;
};

const joinBy = (
  value: unknown,
  picker: (item: any) => string | number | undefined | null
): string => {
  if (!Array.isArray(value) || value.length === 0) return "";
  return value
    .map(picker)
    .filter((item) => item !== undefined && item !== null && item !== "")
    .join(", ");
};

export const getVisibleDataGridColumns = (
  columns: TypeColumn[]
): TypeColumn[] => {
  const savedVisibility = getColumnVisibilityFromStorage(
    DATA_GRID_COLUMN_VISIBILITY_STORAGE_KEY
  );

  return columns.filter((column) => {
    const name = column?.name;
    if (!name) return false;

    if (savedVisibility[name] !== undefined) {
      return savedVisibility[name];
    }

    return column.defaultVisible !== false;
  });
};

export const serializeDataGridCellValue = (
  column: TypeColumn,
  row: Record<string, any>,
  context: CellSerializerContext = {}
): string => {
  const columnName = column?.name;
  if (!columnName) return "";

  const value = row?.[columnName];

  switch (columnName) {
    case "brandId":
      return (
        context.getBrandNameById?.(value) ??
        row?.brandName ??
        (value != null ? String(value) : "")
      );
    case "productTypeId":
      return (
        context.getProductTypeNameById?.(value) ??
        (value != null ? String(value) : "")
      );
    case "shippingMethod":
      return (
        context.getShippingLabelById?.(value) ??
        (value != null ? String(value) : "")
      );
    case "isPublished":
      return value ? "Ja" : "Nej";
    case "createdAt":
    case "modifiedAt":
      return value ? formatDate(value) : "";
    case "categories":
      return joinBy(value, (item) => item?.name);
    case "accessories":
    case "spareParts":
    case "relatedParts":
      return joinBy(value, (item) => item?.mainItemNo);
    case "images":
    case "manuals":
    case "safetySheets":
    case "dimensionalSheets":
      return joinBy(value, (item) => item?.id);
    case "salesChannels":
      return joinBy(value, (item) => item?.name);
    default:
      break;
  }

  if (value == null) return "";

  if (typeof value === "boolean") {
    return value ? "Ja" : "Nej";
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    if (typeof value[0] === "object") {
      return joinBy(
        value,
        (item) => item?.name ?? item?.mainItemNo ?? item?.id
      );
    }
    return value.join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

export const buildDataGridCsvData = (
  dataSource: Record<string, any>[],
  columns: TypeColumn[],
  context: CellSerializerContext = {}
): string[][] => {
  const visibleColumns = getVisibleDataGridColumns(columns);

  const headers = visibleColumns.map(
    (column) => String(column.header ?? column.name ?? "")
  );

  const rows = dataSource.map((row) =>
    visibleColumns.map((column) =>
      serializeDataGridCellValue(column, row, context)
    )
  );

  return [headers, ...rows];
};
